import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { Menu, X, LogOut, User, Home, Globe, Smartphone, Headphones, Calendar, Bell, ClipboardEdit } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { PropCloudAILogo } from './PropCloudAILogo';
import { NotificationBell } from './NotificationBell';
import { NotificationMuteToggle } from './NotificationMuteToggle';
import { useAuth } from '../../contexts/AuthContext';
import { logoutUser } from '../../services/firebase/auth';
import { logActivity } from '../../services/firebase/firestore';
import { ActionType, EntityType } from '../../types/deal.types';
import { UserRole } from '../../types/user.types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { ImpersonationBanner } from '../admin/UserImpersonation/ImpersonationBanner';
import { SupportLauncher } from '../support/SupportLauncher';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { openGetSupport } from '../../utils/supportEvents';
import { AIAssistant } from '../ai/AIAssistant';
import { ReviewMode } from '../screenReview/ReviewMode';
import { AdminNavProvider, useAdminNav } from '../../contexts/AdminNavContext';
import { AdminSidebar } from './AdminSidebar/AdminSidebar';
import { AdminCommandPalette } from './AdminSidebar/AdminCommandPalette';

/**
 * App shell. HQ/admin roles also get the left navigation (AdminSidebar) and
 * the Ctrl/⌘K tool palette — see AdminNavContext.
 */
export const Layout: React.FC = () => (
  <AdminNavProvider>
    <LayoutShell />
  </AdminNavProvider>
);

const LayoutShell: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, isImpersonating } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState('PropCloudAI');
  const [showContactHQ, setShowContactHQ] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { isEnabled } = useFeatureFlags();
  const adminNav = useAdminNav();

  // Get Support (top bar — replaced "Contact HQ" and the floating bottom-left
  // button, Jaco 2026-10-19) opens the screen-annotation mode; the tabbed
  // launcher stays as the fallback when the screenReview flag is off.
  const handleContactHQ = () => {
    if (isEnabled('screenReview')) {
      openGetSupport();
    } else {
      setShowContactHQ(true);
    }
  };

  // Handle click outside to close user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    // Return undefined when menu is not open
    return undefined;
  }, [isUserMenuOpen]);

  useEffect(() => {
    // Load company name from localStorage first for immediate display
    const cachedName = localStorage.getItem('systemCompanyName');
    if (cachedName) {
      setCompanyName(cachedName);
    }

    // Then fetch from Firestore for the most up-to-date value
    const loadCompanyName = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'branding'));
        if (settingsDoc.exists() && settingsDoc.data()?.companyName) {
          const name = settingsDoc.data().companyName;
          setCompanyName(name);
          localStorage.setItem('systemCompanyName', name);
        }
      } catch (error) {
        console.error('Failed to load company name:', error);
      }
    };

    loadCompanyName();

    // Listen for storage events to update company name when changed in another tab/window
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'systemCompanyName' && e.newValue) {
        setCompanyName(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = async () => {
    try {
      if (currentUser && userProfile) {
        await logActivity(
          currentUser.uid,
          userProfile.email,
          ActionType.LOGOUT,
          EntityType.USER,
          currentUser.uid,
          userProfile.email
        );
      }
      
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const contextDisplay = userProfile?.role === UserRole.INDEPENDENT_PARTNER_LVL3 
    ? userProfile?.partnerName || userProfile.displayName 
    : userProfile?.role === UserRole.PARTNER_AGENT_LVL3
    ? userProfile?.tenantName || 'Partner Office'
    : userProfile?.role === UserRole.VIEWING_AGENT
    ? 'Viewing Agent'
    : 'HQ';

  return (
    <div className="min-h-screen bg-neutral-50">
      {isImpersonating && <ImpersonationBanner />}
      <nav className="bg-white shadow-sm glass-blur sticky top-0 border-b-2 border-neutral-400" style={{ zIndex: 10001, marginTop: isImpersonating ? '60px' : '0' }}>
        <div className="px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center space-x-2 mr-4">
                <PropCloudAILogo size="md" />
                <span className="text-xl font-bold text-neutral-900 hidden sm:inline">
                  {companyName}
                </span>
              </Link>
              
              <button
                onClick={() =>
                  adminNav.enabled ? adminNav.setMobileOpen(!adminNav.mobileOpen) : setIsMobileMenuOpen(!isMobileMenuOpen)
                }
                className="md:hidden p-2 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                {isMobileMenuOpen || adminNav.mobileOpen ? (
                  <X className="w-6 h-6 text-neutral-700" />
                ) : (
                  <Menu className="w-6 h-6 text-neutral-700" />
                )}
              </button>
            </div>

            <div className="hidden md:flex items-center space-x-6">
              <Link
                to="/dashboard"
                className="text-neutral-600 hover:text-neutral-900 transition-colors flex items-center space-x-1"
              >
                <Home className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>
              <a
                href="https://propinvest.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-600 hover:text-neutral-900 transition-colors flex items-center space-x-1"
              >
                <Globe className="w-4 h-4" />
                <span>Website</span>
              </a>
              <a
                href="https://mobileapp.propinvest.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-600 hover:text-neutral-900 transition-colors flex items-center space-x-1"
              >
                <Smartphone className="w-4 h-4" />
                <span>Mobile App</span>
              </a>
              <button
                onClick={handleContactHQ}
                title="Get Support — mark up this screen and send it to the support team"
                className="flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-700"
              >
                <ClipboardEdit className="w-4 h-4" />
                <span>Get Support</span>
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {/* Phones: the desktop links are hidden, so Get Support sits here as an icon. */}
              <button
                onClick={handleContactHQ}
                title="Get Support"
                aria-label="Get Support"
                className="md:hidden rounded-full bg-rose-600 p-2 text-white shadow-sm hover:bg-rose-700"
              >
                <ClipboardEdit className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-neutral-600 hidden sm:inline">
                {contextDisplay}
              </span>

              {/* Mute toggle + real-time notification bell */}
              <NotificationMuteToggle />
              <NotificationBell />

              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-1.5 rounded-full hover:bg-neutral-100 transition-colors"
                >
                  <UserAvatar
                    photoURL={userProfile?.photoURL}
                    displayName={userProfile?.displayName || ''}
                    size="sm"
                  />
                  <span className="text-sm font-medium text-neutral-700 hidden md:inline">
                    {userProfile?.firstName} {userProfile?.lastName}
                  </span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg py-1" style={{ zIndex: 10002 }}>
                    <div className="px-4 py-2 border-b border-neutral-100">
                      <p className="text-sm font-medium text-neutral-900">
                        {userProfile?.displayName}
                      </p>
                      <p className="text-xs text-neutral-600">{userProfile?.email}</p>
                    </div>
                    
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    
                    <Link
                      to="/settings"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Calendar className="w-4 h-4" />
                      <span>Calendar Settings</span>
                    </Link>
                    
                    <Link
                      to="/settings?tab=notifications"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Bell className="w-4 h-4" />
                      <span>Notification Settings</span>
                    </Link>
                    
                    <Link
                      to="/my-tickets"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Headphones className="w-4 h-4" />
                      <span>My Tickets</span>
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-error hover:bg-error/5 transition-colors w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && !adminNav.enabled && (
        <div className="md:hidden bg-white border-b border-neutral-200">
          <div className="px-4 py-2 space-y-2">
            <Link
              to="/dashboard"
              className="block py-2 text-neutral-600 hover:text-neutral-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </Link>
            <a
              href="https://propinvest.co.za"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 text-neutral-600 hover:text-neutral-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Website
            </a>
            <a
              href="https://mobileapp.propinvest.co.za"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 text-neutral-600 hover:text-neutral-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Mobile App
            </a>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                handleContactHQ();
              }}
              className="block py-2 text-neutral-600 hover:text-neutral-900 transition-colors w-full text-left"
            >
              Get Support
            </button>
          </div>
        </div>
      )}

      <div className="flex">
        <AdminSidebar
          mobileExtras={
            <div className="space-y-1 text-sm">
              <a href="https://propinvest.co.za" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-neutral-600 hover:bg-neutral-100">
                <Globe className="h-4 w-4" /> Website
              </a>
              <a href="https://mobileapp.propinvest.co.za" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-neutral-600 hover:bg-neutral-100">
                <Smartphone className="h-4 w-4" /> Mobile App
              </a>
              <button
                onClick={() => {
                  adminNav.setMobileOpen(false);
                  handleContactHQ();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-neutral-600 hover:bg-neutral-100"
              >
                <ClipboardEdit className="h-4 w-4" /> Get Support
              </button>
            </div>
          }
        />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
      <AdminCommandPalette />

      {showContactHQ && (
        <SupportLauncher onClose={() => setShowContactHQ(false)} />
      )}

      {/* Screen Review — floating annotate-and-report button (feature-flagged) */}
      <ReviewMode />

      {/* AI Assistant - disabled for now */}
      {/* {currentUser && <AIAssistant />} */}
    </div>
  );
};